import { createAppSelector } from 'mastodon/store';

export const getAncestorsIds = createAppSelector(
  [(_, id: string) => id, (state) => state.contexts.inReplyTos],
  (statusId, inReplyTos) => {
    const ancestorsIds: string[] = [];

    let currentId: string | undefined = statusId;

    while (currentId && !ancestorsIds.includes(currentId)) {
      ancestorsIds.unshift(currentId);
      currentId = inReplyTos[currentId];
    }

    return ancestorsIds;
  },
);

export const getDescendantsIds = createAppSelector(
  [
    (_, id: string) => id,
    (state) => state.contexts.replies,
    (state) => state.statuses,
  ],
  (statusId, contextReplies, statuses) => {
    const descendantsIds: string[] = [];

    const visitIds = [statusId];

    while (visitIds.length > 0) {
      const id = visitIds.pop();

      if (!id) {
        break;
      }

      const replies = contextReplies[id];

      if (statusId !== id) {
        descendantsIds.push(id);
      }

      if (replies) {
        replies.toReversed().forEach((replyId) => {
          if (
            !visitIds.includes(replyId) &&
            !descendantsIds.includes(replyId) &&
            statusId !== replyId
          ) {
            visitIds.push(replyId);
          }
        });
      }
    }

    let insertAt = descendantsIds.findIndex((id) => {
      const status = statuses.get(id);

      if (!status) {
        return false;
      }

      const inReplyToAccountId = status.get('in_reply_to_account_id') as
        | string
        | null;
      const accountId = status.get('account') as string;

      return inReplyToAccountId !== accountId;
    });

    if (insertAt !== -1) {
      descendantsIds.forEach((id, idx) => {
        const status = statuses.get(id);

        if (!status) {
          return;
        }

        const inReplyToAccountId = status.get('in_reply_to_account_id') as
          | string
          | null;
        const accountId = status.get('account') as string;

        if (idx > insertAt && inReplyToAccountId === accountId) {
          descendantsIds.splice(idx, 1);
          descendantsIds.splice(insertAt, 0, id);
          insertAt += 1;
        }
      });
    }

    return descendantsIds;
  },
);

export interface DescendantThreadMeta {
  // Nesting level of each descendant, relative to the focused status.
  // 0 means a direct reply to the focused status.
  depths: Record<string, number>;
  // Ordered list of ancestor ids between each descendant and the focused
  // status (nearest parent first, focused status excluded).
  chains: Record<string, string[]>;
  // Number of loaded transitive descendants below each descendant. Used to
  // label the collapse/expand toggle.
  counts: Record<string, number>;
}

export const getDescendantThreadMeta = createAppSelector(
  [
    (_state, id: string) => id,
    (state) => state.contexts.inReplyTos,
    getDescendantsIds,
  ],
  (rootId, inReplyTos, descendantsIds): DescendantThreadMeta => {
    const depths: Record<string, number> = {};
    const chains: Record<string, string[]> = {};
    const counts: Record<string, number> = {};

    descendantsIds.forEach((id) => {
      const chain: string[] = [];
      let currentId: string | undefined = inReplyTos[id];

      while (currentId && currentId !== rootId && !chain.includes(currentId)) {
        chain.push(currentId);
        currentId = inReplyTos[currentId];
      }

      chains[id] = chain;
      depths[id] = chain.length;

      chain.forEach((ancestorId) => {
        counts[ancestorId] = (counts[ancestorId] ?? 0) + 1;
      });
    });

    return { depths, chains, counts };
  },
);
